import { c as _c } from "react/compiler-runtime";
import { IcoChevronDown16, IcoTrash16 } from '@onefootprint/icons';
import { IconButton, Tooltip } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useEffect, useId, useRef, useState } from 'react';
type OpenState = {
  /** A section the author has just added is empty, so it opens; one that arrives authored stays shut. */
  defaultOpen: boolean;
  open?: never;
  onOpenChange?: never;
} | {
  /** Owned by a parent that opens the section from outside, and keeps its state across remounts. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultOpen?: never;
};
type CtaSectionProps = {
  title: string;
  /** Shown only while the section is shut. */
  summary?: string;
  /** Shown beside the title whether the section is open or shut. */
  badge?: string;
  isSelected?: boolean;
  /** Compact sets the fields closer together. */
  size?: 'default' | 'compact';
  /** Submit must never leave an error sitting inside a shut section. */
  hasError: boolean;
  removeLabel: string;
  removeBlockedReason?: string;
  /** Absent in read only, where the control is gone rather than disabled. */
  onRemove?: () => void;
  children?: React.ReactNode;
} & OpenState;
export const CtaSection = t0 => {
  const $ = _c(75);
  let badge;
  let children;
  let hasError;
  let onRemove;
  let openState;
  let removeBlockedReason;
  let removeLabel;
  let summary;
  let t1;
  let t2;
  let title;
  if ($[0] !== t0) {
    ({
      title,
      summary,
      badge,
      isSelected: t1,
      size: t2,
      hasError,
      removeLabel,
      removeBlockedReason,
      onRemove,
      children,
      ...openState
    } = t0);
    $[0] = t0;
    $[1] = badge;
    $[2] = children;
    $[3] = hasError;
    $[4] = onRemove;
    $[5] = openState;
    $[6] = removeBlockedReason;
    $[7] = removeLabel;
    $[8] = summary;
    $[9] = t1;
    $[10] = t2;
    $[11] = title;
  } else {
    badge = $[1];
    children = $[2];
    hasError = $[3];
    onRemove = $[4];
    openState = $[5];
    removeBlockedReason = $[6];
    removeLabel = $[7];
    summary = $[8];
    t1 = $[9];
    t2 = $[10];
    title = $[11];
  }
  const isSelected = t1 === undefined ? false : t1;
  const size = t2 === undefined ? "default" : t2;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(openState.defaultOpen ?? false);
  const open = openState.open ?? uncontrolledOpen;
  let t3;
  if ($[12] !== openState) {
    t3 = next => {
      if (openState.onOpenChange) {
        openState.onOpenChange(next);
      } else {
        setUncontrolledOpen(next);
      }
    };
    $[12] = openState;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const setOpen = t3;
  const contentId = useId();
  const ref = useRef(null);
  let t4;
  if ($[14] !== hasError || $[15] !== setOpen) {
    t4 = () => {
      if (hasError) {
        setOpen(true);
      }
    };
    $[14] = hasError;
    $[15] = setOpen;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== hasError) {
    t5 = [hasError];
    $[17] = hasError;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  useEffect(t4, t5);
  let t6;
  if ($[19] !== isSelected || $[20] !== setOpen) {
    t6 = () => {
      if (isSelected) {
        setOpen(true);
        ref.current?.scrollIntoView({
          block: "nearest",
          behavior: "smooth"
        });
      }
    };
    $[19] = isSelected;
    $[20] = setOpen;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== isSelected) {
    t7 = [isSelected];
    $[22] = isSelected;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  useEffect(t6, t7);
  const t8 = !isSelected;
  let t9;
  if ($[24] !== isSelected || $[25] !== t8) {
    t9 = cx({
      "border border-tertiary rounded-md": t8,
      "border border-accent rounded-md shadow-[inset_0_0_0_1px_var(--border-color-accent)]": isSelected
    });
    $[24] = isSelected;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== open || $[28] !== setOpen) {
    t10 = () => setOpen(!open);
    $[27] = open;
    $[28] = setOpen;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const t11 = !open;
  let t12;
  if ($[30] !== t11) {
    t12 = cx("shrink-0 transition-transform duration-200", {
      "-rotate-90": t11
    });
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t12) {
    t13 = <IcoChevronDown16 className={t12} />;
    $[32] = t12;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  const t14 = !badge;
  const t15 = !!badge;
  let t16;
  if ($[34] !== t14 || $[35] !== t15) {
    t16 = cx({
      "shrink-0": t14,
      "min-w-0 truncate": t15
    }, "text-label-3 text-primary");
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t16 || $[38] !== title) {
    t17 = <span className={t16}>{title}</span>;
    $[37] = t16;
    $[38] = title;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== badge) {
    t18 = badge && <span aria-hidden={true} className="shrink-0 text-body-3 text-tertiary">{badge}</span>;
    $[40] = badge;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== open || $[43] !== summary) {
    t19 = !open && summary && <span aria-hidden={true} className="truncate text-body-3 text-tertiary">{summary}</span>;
    $[42] = open;
    $[43] = summary;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== contentId || $[46] !== open || $[47] !== t10 || $[48] !== t13 || $[49] !== t17 || $[50] !== t18 || $[51] !== t19) {
    t20 = <button type="button" aria-expanded={open} aria-controls={contentId} onClick={t10} className="flex flex-1 min-w-0 items-center gap-2 cursor-pointer text-left">{t13}{t17}{t18}{t19}</button>;
    $[45] = contentId;
    $[46] = open;
    $[47] = t10;
    $[48] = t13;
    $[49] = t17;
    $[50] = t18;
    $[51] = t19;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== onRemove || $[54] !== removeBlockedReason || $[55] !== removeLabel) {
    t21 = onRemove && <Tooltip content={removeBlockedReason} disabled={!removeBlockedReason}><IconButton icon={IcoTrash16} aria-label={removeLabel} size="compact" destructive={true} disabled={!!removeBlockedReason} onClick={onRemove} /></Tooltip>;
    $[53] = onRemove;
    $[54] = removeBlockedReason;
    $[55] = removeLabel;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== t20 || $[58] !== t21) {
    t22 = <div className="flex items-center gap-2 p-3">{t20}{t21}</div>;
    $[57] = t20;
    $[58] = t21;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  const t23 = !open;
  const t24 = size === "default";
  const t25 = size === "compact";
  let t26;
  if ($[60] !== t24 || $[61] !== t25) {
    t26 = cx("flex flex-col px-3 pb-3", {
      "gap-4": t24,
      "gap-3": t25
    });
    $[60] = t24;
    $[61] = t25;
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  let t27;
  if ($[63] !== children || $[64] !== t26) {
    t27 = <div className={t26}>{children}</div>;
    $[63] = children;
    $[64] = t26;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] !== contentId || $[67] !== t23 || $[68] !== t27) {
    t28 = <div id={contentId} hidden={t23}>{t27}</div>;
    $[66] = contentId;
    $[67] = t23;
    $[68] = t27;
    $[69] = t28;
  } else {
    t28 = $[69];
  }
  let t29;
  if ($[70] !== t22 || $[71] !== t28 || $[72] !== t9 || $[73] !== title) {
    t29 = <fieldset ref={ref} aria-label={title} className={t9}>{t22}{t28}</fieldset>;
    $[70] = t22;
    $[71] = t28;
    $[72] = t9;
    $[73] = title;
    $[74] = t29;
  } else {
    t29 = $[74];
  }
  return t29;
};